import {
  FormField,
  Select,
} from '@cloudscape-design/components';
import {
  LineItemTypes,
  OrderStatus,
  OrderTypes,
  PartTypes,
} from '@parallel-fluidics/shared/constants';
import { getPPartId, pPartFromFirestore } from '@parallel-fluidics/shared/models/projects/parts';
import { useQuery } from '@tanstack/react-query';
import {
  collection,
  documentId,
  getDocs,
  getFirestore,
  query,
  where,
} from 'firebase/firestore';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, {
  useMemo,
  useState,
} from 'react';

import useInternalProjects from '../../../hooks/use-internalProjects';
import useRealtimeDocs from '../../../hooks/use-realtimeDocs';

function ProjectSelector({
  setProject,
  value = null,
  error = '',
  enablePastProjectSearch = false,
  stepType = null,
}) {
  const { internalProjectOptions } = useInternalProjects(enablePastProjectSearch);
  const orderQuery = useMemo(() => query(
    collection(getFirestore(), 'orders'),
    where('status', '==', OrderStatus.IN_PROGRESS),
  ), []);
  const { data: activeOrders, status: activeOrdersStatus } = useRealtimeDocs({ query: orderQuery });

  const [searchText, setSearchText] = useState('');

  const { data: queryProjects = [], isFetching } = useQuery({
    queryKey: ['searchProjects', searchText, enablePastProjectSearch],
    queryFn: async () => {
      // Query on substring of a property value (text search): https://stackoverflow.com/questions/46568142/google-firestore-query-on-substring-of-a-property-value-text-search/56815787#56815787
      const projectsQuery = query(collection(getFirestore(), 'projects'), where('name', '>=', searchText), where('name', '<=', `${searchText}\uf8ff`));
      const querySnapshot = await getDocs(projectsQuery);
      const projects = [];
      if (querySnapshot.empty) return projects;
      await Promise.all(querySnapshot.docs.map(async (doc) => {
        const project = { id: doc.id, ...doc.data() };

        // Add the project without stepPPartId so old logs can be found by project name only.
        // Ensures load config always finds a match if the project name matches.
        projects.push({
          value: project.name,
          projectId: project.name,
          label: `${project.name} - ${project.filename}`,
        });

        const partsQuery = query(
          collection(getFirestore(), 'projects', doc.id, 'parts'),
          where('type', '==', PartTypes.PRODUCT),
        );
        const partsSnapshot = await getDocs(partsQuery);
        if (!partsSnapshot.empty) {
          const parts = partsSnapshot.docs.map(
            (partDoc) => (pPartFromFirestore(partDoc)),
          );
          parts.forEach((part) => {
            if (stepType && part.stepType !== stepType) return;
            const pPartId = getPPartId(part);
            // Handles multiple product parts, allowing process log queries by stepPPartId
            projects.push({
              value: project.name,
              projectId: project.name,
              stepPPart: pPartId,
              label: `${project.name}-${pPartId}`,
            });
          });
        }
      }));
      return projects;
    },
    enabled: !!searchText && enablePastProjectSearch,
  });

  const handleUpdateSearchText = debounce((newSearchText) => {
    setSearchText(newSearchText);
  }, 1000);

  const {
    data: {
      customerOptions: customerLineItemOptions = [],
      internalOptions: internalLineItemOptions = [],
    } = {},
    isPending,
  } = useQuery({
    queryKey: ['lineItems', activeOrders],
    queryFn: async () => {
      const customerOptions = [];
      const internalOptions = [];
      if (!activeOrders?.length) return { customerOptions, internalOptions };

      if (!activeOrders?.length) return [];

      const lineItems = [];
      const orgIdsSet = new Set();

      activeOrders.forEach((order) => {
        order.shipments.forEach((shipment) => {
          if (shipment.isShipped) return;
          shipment.lineItems.forEach((lineItem) => {
            // Product line items have no steps, so runs logged on them reach no traveler.
            if (lineItem.type === LineItemTypes.PRODUCT) return;
            const lineItemId = lineItem.id;
            orgIdsSet.add(order.organization);
            lineItems.push({
              value: [lineItem.projectId, lineItem.pPartId, shipment.targetShipDate].filter(Boolean).join('-'), // the value needs to be unique
              projectId: lineItem.projectId,
              pPartId: lineItem.pPartId,
              projectDocId: lineItem.projectDocId,
              targetShipDate: shipment.targetShipDate,
              organization: order.organization,
              orderId: order.id,
              lineItemId,
              steps: lineItem.steps,
              isInternal: order.isInternal,
            });
          });
        });
      });

      // Sort the line items
      lineItems.sort((a, b) => {
        if (a.targetShipDate !== b.targetShipDate) {
          return a.targetShipDate < b.targetShipDate ? -1 : 1;
        }
        if (a.projectId !== b.projectId) {
          return a.projectId < b.projectId ? -1 : 1;
        }
        if (a.pPartId !== b.pPartId) {
          return (a.pPartId || '') < (b.pPartId || '') ? -1 : 1;
        }
        return 0;
      });

      // Query Firestore for organization names
      const orgIdsArr = Array.from(orgIdsSet);
      const orgsQuery = query(collection(getFirestore(), 'organizations'), where(documentId(), 'in', orgIdsArr));
      const orgsSnapshot = await getDocs(orgsQuery);

      // Create a map of organization IDs to names
      const idOrgMap = {};
      orgsSnapshot.forEach((doc) => {
        const org = doc.data();
        idOrgMap[doc.id] = org.name;
      });

      lineItems.forEach(({ organization, targetShipDate, ...lineItem }) => {
        const label = `${lineItem.projectId}${lineItem.pPartId ? `-${lineItem.pPartId}` : ''} - ${idOrgMap[organization]} - ${targetShipDate}`;
        if (lineItem.isInternal) {
          internalOptions.push({ ...lineItem, label });
        } else {
          customerOptions.push({ ...lineItem, label });
        }
      });

      return { customerOptions, internalOptions };
    },
    enabled: !!activeOrders?.length, // Only run the query if there are customer orders
  });

  const options = useMemo(() => {
    const opts = [
      { label: 'Customer Projects', options: customerLineItemOptions },
      { label: 'Internal Projects', options: internalLineItemOptions },
    ];
    if (queryProjects?.length) {
      opts.unshift({ label: 'Search Results', options: queryProjects });
    }
    // only show old internal projects for load config overlay
    if (internalProjectOptions?.length) {
      opts.push({ label: 'Old Internal Projects', options: internalProjectOptions });
    }
    return opts;
  }, [
    customerLineItemOptions,
    internalLineItemOptions,
    internalProjectOptions,
    queryProjects,
  ]);

  return (
    <FormField
      label="Project"
      description={(
        <span>
          {'Don\'t see the project you\'re looking for? Add customer projects '}
          <a target="_blank" href="/projects" rel="noreferrer">here</a>
          {' or internal projects '}
          <a target="_blank" href={`/projects?type=${OrderTypes.INTERNAL}`} rel="noreferrer">here</a>
          .
        </span>
      )}
      stretch
      errorText={error}
    >
      <Select
        selectedOption={value}
        onChange={({ detail }) => setProject(detail.selectedOption)}
        filteringType={enablePastProjectSearch ? 'manual' : 'auto'}
        onLoadItems={({ detail }) => handleUpdateSearchText(detail.filteringText)}
        options={options}
        loadingText="Loading projects"
        placeholder="Choose a project"
        statusType={
          (activeOrdersStatus === 'loading' || isPending || isFetching)
            ? 'loading' : 'finished'
        }
      />
    </FormField>

  );
}

ProjectSelector.propTypes = {
  setProject: PropTypes.func.isRequired,
  value: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  error: PropTypes.string,
  enablePastProjectSearch: PropTypes.bool,
  stepType: PropTypes.string,
};

export default ProjectSelector;
